import React from 'react';
import { Save, RefreshCw, CheckCircle, Loader2 } from 'lucide-react';
import type { Customer } from '../../types';

export interface CustomerInfoTabProps {
  formData: Partial<Customer>;
  hasChanges: boolean;
  regenerateDocuments: boolean;
  isRegenerating: boolean;
  isSaving: boolean;
  onInputChange: (field: keyof Customer, value: string | number | boolean) => void;
  onRegenerateDocumentsChange: (checked: boolean) => void;
  onSave: () => void;
}

const CustomerInfoTab: React.FC<CustomerInfoTabProps> = ({
  formData,
  hasChanges,
  regenerateDocuments,
  isRegenerating,
  isSaving,
  onInputChange,
  onRegenerateDocumentsChange,
  onSave,
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-gray-900">
          Informations client
        </h2>
        <div className="flex items-center gap-3">
          <label
            className={`flex items-center px-3 py-2 rounded-lg border transition-all ${
              !hasChanges
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : regenerateDocuments
                  ? 'bg-amber-50 border-amber-300 text-amber-700 cursor-pointer'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 cursor-pointer'
            }`}
          >
            <input
              type="checkbox"
              checked={regenerateDocuments}
              onChange={(e) => onRegenerateDocumentsChange(e.target.checked)}
              disabled={!hasChanges}
              className="sr-only"
            />
            <RefreshCw className={`h-4 w-4 mr-2 ${!hasChanges ? 'text-gray-400' : regenerateDocuments ? 'text-amber-600' : 'text-gray-400'}`} />
            <span className="text-sm font-medium">{`R\u00e9g\u00e9n\u00e9rer documents`}</span>
            {regenerateDocuments && hasChanges && (
              <CheckCircle className="h-4 w-4 ml-2 text-amber-600" />
            )}
          </label>
          <button
            onClick={onSave}
            disabled={!hasChanges || isSaving || isRegenerating}
            className="flex items-center px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isRegenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {`R\u00e9g\u00e9n\u00e9ration...`}
              </>
            ) : isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nom
          </label>
          <input
            type="text"
            value={formData.nom || ''}
            onChange={(e) => onInputChange('nom', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {`Pr\u00e9nom`}
          </label>
          <input
            type="text"
            value={formData.prenom || ''}
            onChange={(e) => onInputChange('prenom', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date naissance
          </label>
          <input
            type="text"
            value={formData.dat || ''}
            onChange={(e) => onInputChange('dat', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date format
          </label>
          <input
            type="date"
            value={formData.datF || ''}
            onChange={(e) => onInputChange('datF', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sexe
          </label>
          <select
            value={formData.sexe || ''}
            onChange={(e) => onInputChange('sexe', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">{`S\u00e9lectionner...`}</option>
            <option value="mr">Homme</option>
            <option value="ms">Femme</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            AVS
          </label>
          <input
            type="text"
            value={formData.avs || ''}
            onChange={(e) => onInputChange('avs', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {`\u00c9tat civil`}
          </label>
          <select
            value={formData.civil || ''}
            onChange={(e) => onInputChange('civil', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">{`S\u00e9lectionner...`}</option>
            <option value="C\u00e9libataire">{`C\u00e9libataire`}</option>
            <option value="Mari\u00e9(e)">{`Mari\u00e9(e)`}</option>
            <option value="Divorc\u00e9(e)">{`Divorc\u00e9(e)`}</option>
            <option value="S\u00e9par\u00e9(e)">{`S\u00e9par\u00e9(e)`}</option>
            <option value="Partenariat Enregistr\u00e9">{`Partenariat Enregistr\u00e9`}</option>
            <option value="Partenariat dissous">Partenariat dissous</option>
            <option value="Veuf(ve)">Veuf(ve)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date de mariage
          </label>
          <input
            type="date"
            value={formData.date_civil || ''}
            onChange={(e) => onInputChange('date_civil', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {`Date de s\u00e9paration`}
          </label>
          <input
            type="date"
            value={formData.date_separation || ''}
            onChange={(e) => onInputChange('date_separation', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
      </div>

      <h3 className="text-lg font-medium text-gray-900 mt-8 mb-4">{`Coordonn\u00e9es`}</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={formData.login || ''}
            onChange={(e) => onInputChange('login', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {`T\u00e9l\u00e9phone`}
          </label>
          <input
            type="tel"
            value={formData.tel || ''}
            onChange={(e) => onInputChange('tel', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adresse
          </label>
          <input
            type="text"
            value={formData.adr || ''}
            onChange={(e) => onInputChange('adr', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            NPA
          </label>
          <input
            type="text"
            value={formData.npa || ''}
            onChange={(e) => onInputChange('npa', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {`Localit\u00e9`}
          </label>
          <input
            type="text"
            value={formData.loc || ''}
            onChange={(e) => onInputChange('loc', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pays
          </label>
          <input
            type="text"
            value={formData.pays || ''}
            onChange={(e) => onInputChange('pays', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
      </div>

      <h3 className="text-lg font-medium text-gray-900 mt-8 mb-4">LPP</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ouverture de compte Lemania
          </label>
          <select
            value={formData.onboarding || ''}
            onChange={(e) => onInputChange('onboarding', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">{`S\u00e9lectionner...`}</option>
            <option value="Oui">Oui</option>
            <option value="Non">Non</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Situation professionnelle
          </label>
          <select
            value={formData.sit_pro || ''}
            onChange={(e) => onInputChange('sit_pro', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">{`S\u00e9lectionner...`}</option>
            <option value="Employ\u00e9">{`Employ\u00e9`}</option>
            <option value="Demandeur d'emploi">{"Demandeur d'emploi"}</option>
            <option value="Retrait\u00e9">{`Retrait\u00e9`}</option>
            <option value="Ind\u00e9pendant">{`Ind\u00e9pendant`}</option>
            <option value="Ind\u00e9pendant affili\u00e9">{`Ind\u00e9pendant affili\u00e9 \u00e0 une caisse de pension`}</option>
            <option value="Ind\u00e9pendant non affili\u00e9">{`Ind\u00e9pendant non affili\u00e9 \u00e0 un fond de pension`}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Le client souhaite
          </label>
          <select
            value={formData.souhait || ''}
            onChange={(e) => onInputChange('souhait', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">{`S\u00e9lectionner...`}</option>
            <option value="recherche">Une recherche</option>
            <option value="regroupement">Un regroupement</option>
            <option value="recherche et regroupement">Une recherche et un regroupement</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {`Montant \u00e0 la suppl\u00e9tive`}
          </label>
          <select
            value={formData.catSuppletive || ''}
            onChange={(e) => onInputChange('catSuppletive', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            <option value="">{`S\u00e9lectionner...`}</option>
            <option value="+1300">Plus de 1300.- CHF</option>
            <option value="-1300">Moins de 1300.- CHF</option>
            <option value="0">{"Pas d'avoirs"}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {`Montant r\u00e9el \u00e0 la suppl\u00e9tive`}
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.montantSuppletive || ''}
            onChange={(e) => onInputChange('montantSuppletive', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoTab;
